import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, Phone } from 'lucide-react';

export const AuthModal = ({ open, onOpenChange }) => {
  const { login, signup } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    phone: '' 
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(loginForm.email, loginForm.password);
      toast.success(t('auth_login_success'));
      onOpenChange(false);
      setLoginForm({ email: '', password: '' });
    } catch (error) {
      const message = error.response?.data?.detail || t('auth_login_error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await signup(
        signupForm.name, 
        signupForm.email, 
        signupForm.password,
        signupForm.phone
      );
      toast.success(t('auth_signup_success'));
      onOpenChange(false);
      setSignupForm({ name: '', email: '', password: '', phone: '' });
    } catch (error) {
      const message = error.response?.data?.detail || t('auth_signup_error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight uppercase text-center">
            {t('auth_title')}
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            {t('auth_description')}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-muted">
            <TabsTrigger 
              value="login" 
              className="uppercase tracking-wider font-semibold"
              data-testid="login-tab"
            >
              {t('auth_login_tab')}
            </TabsTrigger>
            <TabsTrigger 
              value="signup" 
              className="uppercase tracking-wider font-semibold"
              data-testid="signup-tab"
            >
              {t('auth_signup_tab')}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="login" className="mt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  {t('auth_email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="pl-10 h-12 bg-input/50 border-transparent focus:border-primary"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  {t('auth_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="pl-10 h-12 bg-input/50 border-transparent focus:border-primary"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-wider hover:bg-primary/90 btn-glow"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t('auth_login_btn')
                )}
              </Button>
            </form>
          </TabsContent>
          
          <TabsContent value="signup" className="mt-6">
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signup-name" className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  {t('auth_name')}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="Votre nom"
                    value={signupForm.name}
                    onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                    className="pl-10 h-12 bg-input/50 border-transparent focus:border-primary"
                    required
                    data-testid="signup-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signup-email" className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  {t('auth_email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={signupForm.email}
                    onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                    className="pl-10 h-12 bg-input/50 border-transparent focus:border-primary"
                    required
                    data-testid="signup-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signup-phone" className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  {t('auth_phone')}
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-phone"
                    type="tel"
                    placeholder="+243 XXX XXX XXX"
                    value={signupForm.phone}
                    onChange={(e) => setSignupForm({ ...signupForm, phone: e.target.value })}
                    className="pl-10 h-12 bg-input/50 border-transparent focus:border-primary"
                    data-testid="signup-phone-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signup-password" className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  {t('auth_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={signupForm.password}
                    onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                    className="pl-10 h-12 bg-input/50 border-transparent focus:border-primary"
                    required
                    minLength={6}
                    data-testid="signup-password-input"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-wider hover:bg-primary/90 btn-glow"
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t('auth_signup_btn')
                )}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
